import { util } from "/modules/util.js";
import {
	HTMLTemplateResult,
	nothing,
	PropertyValues,
} from "/modules/vendor/lit.js";

import { BaseElement } from "/modules/components/base-element.js";
import {
	customElement,
	property,
	state,
} from "/modules/vendor/lit/decorators.js";

import { sessionStorageUtil } from "/modules/session-storage-util.js";
import { CSVRowStates } from "/modules/components/csv-import/csv-import-view.js";

export enum MessageType {
	Info,
	Success,
	Warning,
	Error,
	Help,
	// adds info text automatically.
	// TODO report to a support backend? maybe create github issue
	InternalError, // TODO or FatalError?
}

export enum NotificationType {
	Toast, // with timeout, for example on successful save
	Notification, // no timeout, for example when user has to read the message
	Dialog, // modal with actions
}

class Notification {
	messageType!: MessageType;
	title!: string;
	description?: string | HTMLTemplateResult;
	createdAt: number;

	constructor(
		messageType: MessageType,
		title: string,
		description?: string | HTMLTemplateResult,
	) {
		this.messageType = messageType;
		this.title = title;
		this.description = description;
		this.createdAt = Date.now();
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"komit-notifier": Notifier;
	}
}

@customElement("komit-notifier")
export class Notifier extends BaseElement {
	static styles = [BaseElement.styles];

	// necessary for workaround, see comment in updated method
	@property({
		type: Boolean,
		reflect: true,
	})
	inert: boolean = false;

	@state()
	private notifications: Notification[] = [];

	private isInternalNavigationTriggered = false;
	private _portalContainer: HTMLDivElement | null = null;
	private _dialogStack: HTMLDialogElement[] = [];

	// important that arrow function and not registered as event listener
	// with `bind(this)` to prevent memory leaks
	private handleBeforeUnload = (e: CustomEvent): void => {
		// handles for example case when someone comes back and super admin session is expired;
		// or was added to another org unit and got new permissions
		sessionStorageUtil.unsafeClearPermissionsCacheOnly();

		if (this.isInternalNavigationTriggered) {
			this.isInternalNavigationTriggered = false;
			return;
		}

		// Prevent closing the page in case csv import is not finished.
		// There are side effects:
		// - The prompt is triggered on page reload
		// - The prompt is not triggered in case no action was done on the page,
		// 		thus will not be triggered right after the page has been
		// 		refreshed and no action has been done after that refresh.
		let rowStates = sessionStorageUtil.get<CSVRowStates>(
			sessionStorageUtil.csvImportMetaKey,
		);
		if (rowStates && Object.keys(rowStates).length > 0) {
			e.preventDefault();
		}
	};

	private handleDialogOpened = (e: Event) => {
		const dialogEl = (e as CustomEvent).detail
			?.dialogEl as HTMLDialogElement;
		if (!dialogEl || !this._portalContainer) return;

		this._dialogStack.push(dialogEl);
		dialogEl.appendChild(this._portalContainer);
	};

	private handleDialogClosed = () => {
		if (!this._portalContainer) return;

		this._dialogStack.pop();

		if (this._dialogStack.length > 0) {
			this._dialogStack[this._dialogStack.length - 1].appendChild(
				this._portalContainer,
			);
		} else {
			document.body.appendChild(this._portalContainer);
		}
	};

	connectedCallback() {
		super.connectedCallback();

		this._portalContainer = document.createElement("div");
		Object.assign(this._portalContainer.style, {
			position: "fixed",
			inset: "0",
			width: "100vw",
			height: "100vh",
			zIndex: "2147483647",
			margin: "0",
			padding: "0",
			border: "none",
			background: "transparent",
			overflow: "visible",
			pointerEvents: "none",
		});
		document.body.appendChild(this._portalContainer);

		document.addEventListener("dialog-opened", this.handleDialogOpened);
		document.addEventListener("dialog-closed", this.handleDialogClosed);

		// important that arrow function and not registered as event listener
		// with `bind(this)` to prevent memory leaks
		window.addEventListener(
			"beforeunload",
			this.handleBeforeUnload as EventListener,
		);

		this.checkForMessageAfterReload();

		/*
		this.notify(MessageType.Info, "first", "info");
		this.notify(MessageType.Success, "second", "success");
		this.notify(MessageType.Warning, "third", "warning");
		this.notify(MessageType.Error, "forth", "error");
		this.notify(MessageType.Help, "fifth", "help");
		 */
	}

	disconnectedCallback() {
		this._portalContainer?.remove();
		this._portalContainer = null;
		this._dialogStack = [];

		document.removeEventListener("dialog-opened", this.handleDialogOpened);
		document.removeEventListener("dialog-closed", this.handleDialogClosed);

		// important that arrow function and not registered as event listener
		// with `bind(this)` to prevent memory leaks
		window.removeEventListener(
			"beforeunload",
			this.handleBeforeUnload as EventListener,
		);

		// sessionStorageUtil.rollbackTransaction(); was moved here from
		// "handleBeforeUnload" callback, as a new prompt was added to ask user
		// confirmation before leaving the page. The transaction is rolled back
		// otherwise if user clicks "No" on the prompt. (which is not expected.)
		//
		// 								↓↓↓
		//
		// the rollback request might fail because the response cannot be read,
		// but shouldn't matter because in most cases it was already sent to and
		// received/processed by server
		//
		// only rollback and not clearing sessionStorage because if a user
		// goes to another website and then navigates back to Komit, the
		// sessionStorage is still valid
		//
		// rollback is still useful for the CSV import implementation, but
		// might not fit for other use cases and might need a reevaluation in
		// the future
		sessionStorageUtil.rollbackTransaction();

		super.disconnectedCallback();
	}

	protected updated(_changedProperties: PropertyValues) {
		super.updated(_changedProperties);

		// workaround to make notifications closable when a modal is opened;
		// modal sets inert on elements (except on itself and the own children)
		// when its opened, which makes to close button unclickable
		if (_changedProperties.has("inert") && this.inert === true) {
			this.inert = false;
		}

		if (!this._portalContainer) return;

		this.renderNotifications();
	}

	private renderNotifications() {
		if (!this._portalContainer) return;

		const wrapper = document.createElement("div");
		Object.assign(wrapper.style, {
			position: "absolute",
			top: "0",
			right: "0",
			padding: "1.5rem 1rem",
			width: "20rem",
			maxWidth: "100%",
			textAlign: "left",
			boxSizing: "border-box",
		});

		for (const notification of this.notifications) {
			const card = document.createElement("div");
			Object.assign(card.style, {
				pointerEvents: "auto",
				cursor: "pointer",
				marginBottom: "1rem",
				overflow: "hidden",
				borderRadius: "0.375rem",
				background: "#fff",
				boxShadow:
					"0 10px 15px -3px rgba(0,0,0,.1), 0 4px 6px -4px rgba(0,0,0,.1), 0 0 0 1px rgba(0,0,0,.05)",
				border: `1px solid ${this.borderColor(notification.messageType)}`,
				boxSizing: "border-box",
			});
			card.addEventListener("click", (e: Event) => {
				e.stopPropagation();
				e.stopImmediatePropagation();
				this.close(notification);
			});

			const inner = document.createElement("div");
			inner.style.padding = "1rem";

			const row = document.createElement("div");
			Object.assign(row.style, {
				display: "flex",
				alignItems: "flex-start",
			});

			const iconWrap = document.createElement("div");
			iconWrap.style.flexShrink = "0";
			const iconEl = document.createElement("komit-icon");
			iconEl.style.color = this.iconColor(notification.messageType);
			iconEl.setAttribute("icon", this.icon(notification.messageType));
			iconWrap.appendChild(iconEl);
			row.appendChild(iconWrap);

			const textCol = document.createElement("div");
			Object.assign(textCol.style, {
				marginLeft: "0.75rem",
				width: "0",
				flex: "1",
				paddingTop: "0.125rem",
			});

			const title = document.createElement("p");
			Object.assign(title.style, {
				fontSize: "0.875rem",
				fontWeight: "500",
				color: "#111827",
				margin: "0",
			});
			title.textContent =
				typeof notification.title === "string"
					? notification.title
					: "";
			textCol.appendChild(title);

			if (
				notification.description &&
				notification.title != notification.description
			) {
				const desc = document.createElement("p");
				Object.assign(desc.style, {
					marginTop: "0.25rem",
					fontSize: "0.875rem",
					color: "#6b7280",
					marginBottom: "0",
				});
				if (typeof notification.description === "string") {
					desc.textContent = notification.description;
				} else {
					desc.textContent = "";
				}
				textCol.appendChild(desc);
			}

			if (notification.messageType == MessageType.InternalError) {
				const support = document.createElement("p");
				Object.assign(support.style, {
					marginTop: "0.25rem",
					fontSize: "0.875rem",
					color: "#6b7280",
					marginBottom: "0",
				});
				support.textContent = util.tr(
					"Please contact the support if this error occurs again.",
				);
				textCol.appendChild(support);
			}

			row.appendChild(textCol);

			if (this.timeout(notification.messageType) <= 0) {
				const closeWrap = document.createElement("div");
				Object.assign(closeWrap.style, {
					marginLeft: "1rem",
					display: "flex",
					flexShrink: "0",
				});
				const closeBtn = document.createElement("button");
				Object.assign(closeBtn.style, {
					display: "inline-flex",
					borderRadius: "2px",
					color: "#9ca3af",
					border: "none",
					background: "none",
					cursor: "pointer",
				});
				closeBtn.innerHTML = `<svg style="width:1.25rem;height:1.25rem;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>`;
				closeWrap.appendChild(closeBtn);
				row.appendChild(closeWrap);
			}

			inner.appendChild(row);
			card.appendChild(inner);
			wrapper.appendChild(card);
		}

		this._portalContainer.replaceChildren(wrapper);
	}

	reloadAndNotify(
		messageType: MessageType,
		title: string,
		description?: string,
	): void {
		sessionStorageUtil.set<Notification>(sessionStorageUtil.notifierKey, {
			messageType: messageType,
			title: title,
			description: description,
			createdAt: Date.now(),
		});

		this.onInternalNavigationTriggered();
		window.location.replace(window.location.pathname);
	}

	redirectAndNotify(
		url: string,
		messageType: MessageType,
		title: string,
		description?: string,
	): void {
		sessionStorageUtil.set<Notification>(sessionStorageUtil.notifierKey, {
			messageType: messageType,
			title: title,
			description: description,
			createdAt: Date.now(),
		});

		this.onInternalNavigationTriggered();
		window.location.replace(url);
	}

	private onInternalNavigationTriggered() {
		this.isInternalNavigationTriggered = true;
		setTimeout(() => {
			this.isInternalNavigationTriggered = false;
		});
	}

	checkForMessageAfterReload(): void {
		const notification = sessionStorageUtil.get<Notification>(
			sessionStorageUtil.notifierKey,
		);
		sessionStorageUtil.set(sessionStorageUtil.notifierKey, {});
		if (notification?.messageType && notification?.title) {
			this.notify(
				notification.messageType,
				notification.title,
				notification.description,
			);
		}
	}

	notify(
		messageType: MessageType,
		title: string,
		description?: string | HTMLTemplateResult,
	) {
		let newNotification = new Notification(messageType, title, description);
		this.notifications.unshift(newNotification);

		const errorTypes = [MessageType.Error, MessageType.InternalError];
		if (errorTypes.includes(messageType)) {
			this.notifications = this.notifications.filter(
				(n, i) => i === 0 || !errorTypes.includes(n.messageType),
			);
		}

		if (messageType == MessageType.Success) {
			let deadline = Date.now() - 7.5 * 1000; // Date.now() is in milliseconds

			// close all messages that have no timeout and are older than 10 seconds on success;
			// this behaviour makes sense for the evaluated cases, but it could be, that it's
			// necessary to apply more fine grained rules for some use cases
			for (const notification of this.notifications) {
				if (
					this.timeout(notification.messageType) == -1 &&
					notification.createdAt < deadline
				) {
					this.close(notification);
				}
			}
		}

		// TODO requestUpdate shouldn't be necessary and is not necessary when notify is called from within component
		// 		for example for tests in connectedCallback
		// 		but if notify is called via util.notify, requestUpdate is necessary so that they show
		// 		however, closing on timeout works as expected
		this.requestUpdate();
		this.updateComplete.then(() => {
			let timeout = this.timeout(messageType);
			if (timeout > 0) {
				setTimeout(() => {
					this.close(newNotification);
				}, timeout);
			}
		});
	}

	closeAllWithoutTimeout() {
		this.notifications = this.notifications.filter(
			(notification) => this.timeout(notification.messageType) != -1,
		);
	}

	private timeout(messageType: MessageType): number {
		switch (messageType) {
			case MessageType.Success:
				return 2500;
			case MessageType.Info:
			case MessageType.Warning:
			case MessageType.Help:
			case MessageType.Error:
			case MessageType.InternalError:
				// notify() relies on this value
				return 7500;
		}
	}

	private iconColor(messageType: MessageType): string {
		switch (messageType) {
			case MessageType.Info:
			case MessageType.Help:
				return "#60a5fa";
			case MessageType.Success:
				return "#4ade80";
			case MessageType.Warning:
				return "#facc15";
			case MessageType.Error:
			case MessageType.InternalError:
				return "#f87171";
		}
	}

	private borderColor(messageType: MessageType): string {
		switch (messageType) {
			case MessageType.Info:
			case MessageType.Help:
				return "#60a5fa";
			case MessageType.Success:
				return "#4ade80";
			case MessageType.Warning:
				return "#facc15";
			case MessageType.Error:
			case MessageType.InternalError:
				return "#f87171";
		}
	}

	private icon(messageType: MessageType): string {
		switch (messageType) {
			case MessageType.Info:
				return "fa-jelly fa-regular fa-info-circle";
			case MessageType.Success:
				return "fa-jelly fa-regular fa-circle-check";
			case MessageType.Warning:
			case MessageType.Error:
			case MessageType.InternalError:
				return "fa-jelly fa-regular fa-circle-xmark";
			case MessageType.Help:
				return "fa-jelly fa-regular fa-circle-question";
		}
	}

	private close(notification: Notification) {
		this.notifications = this.notifications.filter((notificationx) => {
			return notificationx != notification;
		});
	}

	render() {
		return nothing;
	}
}
